"use client";

import { type RefObject, useEffect, useState } from "react";

/**
 * The frame size captions are laid out and rendered at when the media has **no
 * video track** (audio-only). Audio has no intrinsic resolution, so preview and
 * export must agree on one canvas size — keep this the single source for both
 * (the live overlay and the Lambda export's audio default).
 */
export const AUDIO_RENDER_SIZE = { width: 1920, height: 1080 } as const;

export interface ContentRect {
  left: number;
  top: number;
  width: number;
  height: number;
  /**
   * Intrinsic media resolution (`videoWidth`×`videoHeight`). This is the basis
   * the burn/Lambda export lays captions out at, so callers that must match the
   * export (caption overlay, selection box) measure/draw at this resolution and
   * scale down by `width / sourceWidth` — `measureText` isn't linear across font
   * sizes, so wrapping only matches when both sides lay out at the same size.
   */
  sourceWidth: number;
  sourceHeight: number;
}

/** The `object-contain` picture rect of a `vw×vh` video shown in a `cw×ch` box. */
export function containRect(vw: number, vh: number, cw: number, ch: number): ContentRect {
  const scale = Math.min(cw / vw, ch / vh);
  const width = vw * scale;
  const height = vh * scale;
  return {
    left: (cw - width) / 2,
    top: (ch - height) / 2,
    width,
    height,
    sourceWidth: vw,
    sourceHeight: vh,
  };
}

/**
 * Track the letterboxed content rect (CSS px, relative to the element's own box)
 * of an `object-contain` `<video>`, updating on resize and metadata. Returns null
 * until dimensions are known. Cheap: recomputes on those events, not per frame —
 * the content rect only changes when the element or video size does.
 */
export function useVideoContentRect(
  videoRef: RefObject<HTMLVideoElement | HTMLAudioElement | null>,
  /**
   * Intrinsic size to fall back to when the element reports no video dimensions
   * (audio-only media). Pass {@link AUDIO_RENDER_SIZE} for audio runs so the
   * caption frame still has a content rect; omit for video.
   */
  fallbackSize?: { width: number; height: number }
): ContentRect | null {
  const [rect, setRect] = useState<ContentRect | null>(null);
  const fw = fallbackSize?.width;
  const fh = fallbackSize?.height;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !(el instanceof HTMLVideoElement)) return;
    const measure = () => {
      const vw = el.videoWidth || fw || 0;
      const vh = el.videoHeight || fh || 0;
      const { clientWidth: cw, clientHeight: ch } = el;
      if (!vw || !vh || !cw || !ch) return;
      setRect(containRect(vw, vh, cw, ch));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    el.addEventListener("loadedmetadata", measure);
    return () => {
      observer.disconnect();
      el.removeEventListener("loadedmetadata", measure);
    };
  }, [videoRef, fw, fh]);

  return rect;
}
